// MOCK ONLY. Real sampler is server-side. Do not extend, document, or reuse this file.
//
// Purpose: produce a batch of candidate inputs for the mock campaign router.
// Strategy: initial Latin-Hypercube design, then greedy next-batch selection
// using the trivial surrogate in ./surrogate.ts.
//
// score: sigma * (0.1 + |mu|/max|mu|)
//
// Deliberately weak. Do not optimise.

import type { AxisSpec, OutputSpec, SamplePoint } from "../types";
import { fit, predict, type TrainedSurrogate } from "./surrogate";

// ---------------------------------------------------------------------------
// Deterministic PRNG: mulberry32. Seeded from campaign id so runs are
// reproducible. Simple, stateless enough for mock usage.
// ---------------------------------------------------------------------------

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function makeRng(campaignId: string, salt = "batch") {
  return mulberry32(hashSeed(`${campaignId}:${salt}`));
}

// ---------------------------------------------------------------------------
// Axis helpers
// ---------------------------------------------------------------------------

export function rangeAxes(inputs: AxisSpec[]): AxisSpec[] {
  return inputs.filter((a) => a.mode === "range");
}

export function fixedValues(inputs: AxisSpec[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const a of inputs) {
    if (a.mode === "fixed" && a.fixedValue != null) {
      out[a.name] = a.fixedValue;
    }
  }
  return out;
}

function fillFixed(
  rangeVals: Record<string, number>,
  inputs: AxisSpec[],
): Record<string, number> {
  return { ...fixedValues(inputs), ...rangeVals };
}

// ---------------------------------------------------------------------------
// Initial Latin-Hypercube design over the range-mode axes.
// ---------------------------------------------------------------------------

export function initialDesign(
  inputs: AxisSpec[],
  rng: () => number,
): Array<Record<string, number>> {
  const range = rangeAxes(inputs);
  if (range.length === 0) return [];
  const nSamples = 2 * range.length;

  // For each axis, build a shuffled list of stratified bin centres.
  const perAxis = range.map((axis) => {
    const lo = axis.min ?? 0;
    const hi = axis.max ?? 1;
    const bins: number[] = [];
    for (let i = 0; i < nSamples; i++) {
      const u = (i + rng()) / nSamples;
      bins.push(lo + u * (hi - lo));
    }
    // Fisher-Yates shuffle.
    for (let i = bins.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const a = bins[i] ?? 0;
      const b = bins[j] ?? 0;
      bins[i] = b;
      bins[j] = a;
    }
    return bins;
  });

  const samples: Array<Record<string, number>> = [];
  for (let k = 0; k < nSamples; k++) {
    const point: Record<string, number> = {};
    range.forEach((axis, idx) => {
      const col = perAxis[idx] ?? [];
      point[axis.name] = col[k] ?? 0;
    });
    samples.push(fillFixed(point, inputs));
  }
  return samples;
}

// ---------------------------------------------------------------------------
// Greedy batch selection.
// ---------------------------------------------------------------------------

function normalisePoint(
  point: Record<string, number>,
  range: AxisSpec[],
): number[] {
  return range.map((axis) => {
    const lo = axis.min ?? 0;
    const hi = axis.max ?? 1;
    const span = hi - lo || 1;
    const v = point[axis.name] ?? 0;
    return (v - lo) / span;
  });
}

function candidateGrid(
  range: AxisSpec[],
  rng: () => number,
  count = 400,
): Array<Record<string, number>> {
  const out: Array<Record<string, number>> = [];
  for (let i = 0; i < count; i++) {
    const pt: Record<string, number> = {};
    for (const axis of range) {
      const lo = axis.min ?? 0;
      const hi = axis.max ?? 1;
      pt[axis.name] = lo + rng() * (hi - lo);
    }
    out.push(pt);
  }
  return out;
}

type PerOutputModel = { name: string; model: TrainedSurrogate; weight: number };

function fitAllOutputs(
  inputs: AxisSpec[],
  outputs: OutputSpec[],
  observed: SamplePoint[],
): PerOutputModel[] {
  const range = rangeAxes(inputs);
  if (observed.length === 0 || range.length === 0) {
    return outputs.map((o) => ({
      name: o.name,
      weight: o.weight,
      model: fit([], []),
    }));
  }

  const xsAll = observed.map((p) => normalisePoint(p.inputs, range));

  return outputs.map((o) => {
    const ys: number[] = [];
    const xs: number[][] = [];
    for (let i = 0; i < observed.length; i++) {
      const p = observed[i];
      const row = xsAll[i];
      if (!p || !row) continue;
      const v = p.outputs?.[o.name];
      if (v != null && Number.isFinite(v)) {
        ys.push(v);
        xs.push(row);
      }
    }
    return { name: o.name, weight: o.weight, model: fit(xs, ys) };
  });
}

function scoreAt(
  xNorm: number[],
  perOutput: PerOutputModel[],
  maxAbsMu: Record<string, number>,
  disabled: Set<string> = new Set(),
): number {
  let total = 0;
  let wSum = 0;
  for (const { name, model, weight } of perOutput) {
    if (disabled.has(name)) continue;
    const { mean, sigma } = predict(model, xNorm);
    const denom = Math.max(maxAbsMu[name] ?? 1, 1e-9);
    const contrib = sigma * (0.1 + Math.abs(mean) / denom);
    total += weight * contrib;
    wSum += weight;
  }
  return wSum > 0 ? total / wSum : 0;
}

// Approximate max-|mu| over a light random sample for score normalisation.
function computeMaxAbsMu(
  range: AxisSpec[],
  perOutput: PerOutputModel[],
  rng: () => number,
): Record<string, number> {
  const out: Record<string, number> = {};
  const samples = candidateGrid(range, rng, 50);
  for (const { name, model } of perOutput) {
    let m = 1e-6;
    for (const s of samples) {
      const x = normalisePoint(s, range);
      const { mean } = predict(model, x);
      const a = Math.abs(mean);
      if (a > m) m = a;
    }
    out[name] = m;
  }
  return out;
}

export function proposeBatch(
  campaignId: string,
  inputs: AxisSpec[],
  outputs: OutputSpec[],
  observed: SamplePoint[],
  batchSize: number,
  iteration: number,
): Array<Record<string, number>> {
  const range = rangeAxes(inputs);
  if (range.length === 0) return [];

  const rng = makeRng(campaignId, `iter-${iteration}`);

  // Cold start: LHS initial design, optionally padded to batchSize.
  if (observed.length === 0) {
    const seed = initialDesign(inputs, rng);
    const out: Array<Record<string, number>> = [];
    if (seed.length === 0) return out;
    for (let i = 0; i < batchSize; i++) {
      const s = seed[i % seed.length];
      if (s) out.push(s);
    }
    return out.slice(0, batchSize);
  }

  // Fit per-output surrogates on observations.
  let perOutput = fitAllOutputs(inputs, outputs, observed);
  let maxAbsMu = computeMaxAbsMu(range, perOutput, rng);

  const picks: Array<Record<string, number>> = [];
  const hallucinated: SamplePoint[] = [];

  for (let b = 0; b < batchSize; b++) {
    const candidates = candidateGrid(range, rng, 400);
    let bestScore = -Infinity;
    let bestPoint: Record<string, number> | null = null;
    let bestX: number[] | null = null;
    for (const cand of candidates) {
      const xn = normalisePoint(cand, range);
      const s = scoreAt(xn, perOutput, maxAbsMu);
      if (s > bestScore) {
        bestScore = s;
        bestPoint = cand;
        bestX = xn;
      }
    }
    if (!bestPoint || !bestX) break;
    picks.push(fillFixed(bestPoint, inputs));

    // Hallucinate observation using current surrogate means and refit so the
    // next pick diversifies.
    const hallOutputs: Record<string, number> = {};
    for (const { name, model } of perOutput) {
      hallOutputs[name] = predict(model, bestX).mean;
    }
    const stub: SamplePoint = {
      id: `__hallucinated_${b}`,
      iter: iteration,
      batchId: "__stub__",
      status: "done",
      source: "sampler",
      inputs: bestPoint,
      outputs: hallOutputs,
    };
    hallucinated.push(stub);
    perOutput = fitAllOutputs(inputs, outputs, [...observed, ...hallucinated]);
    maxAbsMu = computeMaxAbsMu(range, perOutput, rng);
  }

  return picks;
}

// Export the scoring helper and surrogate fitter for the router's surface /
// score endpoints — keeps all the mock-only vocabulary confined to this module
// tree.
export { fitAllOutputs, scoreAt, computeMaxAbsMu, normalisePoint };
export type { PerOutputModel };
